import { z } from 'zod';
import { findCurrencyMinorUnits } from '../currencies.ts';
import { divHalfUp, roundToEnding } from './decimal.ts';
import type { ListColumn, ListColumnEdit, ListColumnType, ListEditOperation } from './contract.ts';
import { PIECE_UNIT } from '../pack-unit.ts';
import { formatListDate, formatListValue, isListMoneyValue, optionLabel } from './describe.ts';
import { listFilterGroupSchema, type ListFilterGroup } from './filter.ts';
import { evaluateFormula, formulaFitsType, parseFormula, type FormulaValue, type ParsedFormula } from './formula.ts';
import { computePriceFromPurchase, decimalToUnits4, formatFactor, parsePurchasePriceFactor, type PurchasePriceBase, type PurchasePriceRate } from './purchase-price-rule.ts';

/**
 * Operationen der Massenbearbeitung (AD-32, Story 1.21, EXPERIENCE §4.9) als reine Funktionen: prüfen, rechnen,
 * beschreiben. Ohne Datenbank, Uhr und Node-Abhängigkeiten (auch die Web-App nutzt sie).
 *
 * Werte je Spaltentyp:
 * - `text`: Text oder `null` (leer).
 * - `number`, `quantity`, `percent`: Dezimaltext mit Punkt (z. B. "12.5"), höchstens 4 Nachkommastellen,
 *   kaufmännisch gerundet (half-up). Nie Gleitkomma: gerechnet wird über BigInt.
 * - `money`: ganze Zahl in der kleinsten Einheit (Cent) als Text, wie in den Listen (AD-24). Eingegebene Beträge
 *   (setzen, + / − Betrag) sind in Euro („99.90“ bzw. „99,90“) und werden auf ganze Cent gerundet.
 *   Ausnahme Story 5.60: Geld-Spalte mit `edit.scale: 4` (VK netto) – Euro-Dezimaltext mit genau 4 Nachkommastellen
 *   („12.3456“, „40.9000“), gerechnet auf 10⁻⁴ €; eine reine Ziffernfolge („3890“) ist dort Cent (Altbestand vor 5.60).
 *   Abnahme durch Steuerberater/Rudolf nötig (Preislogik).
 * - `enum`, `status`: technischer Wert aus `options` oder `null`.
 * - `multi_enum`: Liste technischer Werte.
 * - `boolean`: `true` / `false`.
 * - `date`, `datetime`: ISO-Datum (`2027-02-14`) bzw. Zeitpunkt oder `null`.
 *
 * Runden (`round`, auch als Zusatz zu setzen und + / −): auf ,90 · ,99 · ganze – zum nächstgelegenen Wert mit
 * dieser Endung, bei Gleichstand aufwärts.
 *
 * Story 1.30 (AD-32 Ergänzung 04.10.2026, EXPERIENCE §4.12): Modi je Feldzeile zusätzlich zu den Aktionen der Spalte –
 * `formula` (Formel mit Spaltenbezügen, `formula.ts`), `rule_table` (Bedingungen, erste passende gilt), `template`
 * (Text-Vorlage mit Spalten), `copy_from` (Wert einer anderen Spalte), `paste` (eingefügte Werte als Hand-Werte; ohne
 * eingefügten Wert bleibt die Zeile, wie sie ist); je Feldzeile optional `where` („nur wenn …“, Filter-Beschreibung).
 * Bedingungen (`where`, Regel-Tabelle) prüft `reporting` mit derselben Abfrage wie die Liste und gibt hier nur das
 * Ergebnis hinein (welche Regel gegriffen hat). Suchen/Ersetzen mit mehreren Paaren, Groß/Klein und „nur ganzes Wort“.
 */

/** Modi der Feldzeile über die Aktionen der Spalte hinaus (Story 1.30). Gelten für jede passende Spalte mit `edit`. */
export const listEditModes = ['formula', 'rule_table', 'template', 'copy_from', 'paste'] as const;
export type ListEditMode = (typeof listEditModes)[number];
/** Aktion oder Modus einer Feldzeile. */
export type ListEditOp = ListEditOperation | ListEditMode;

export const listEditModeLabels: Record<ListEditMode, string> = {
  formula: 'Formel',
  rule_table: 'Regel-Tabelle',
  template: 'Vorlage',
  copy_from: 'aus Spalte',
  paste: 'Werte einfügen',
};

/** Runden auf „,90“, „,99“ oder ganze Zahlen. */
export const listEditRounds = ['x90', 'x99', 'whole'] as const;
export type ListEditRound = (typeof listEditRounds)[number];

export const listEditRoundLabels: Record<ListEditRound, string> = { x90: 'auf ,90', x99: 'auf ,99', whole: 'auf ganze' };

/**
 * Story 5.85: Grundlage von „aus EK berechnen“ – EK des Hauptlieferanten oder kalkulatorischer Einstandspreis (FR-134).
 * Fehlt die Grundlage (Anfrage oder gespeicherte Vorlage vor 5.85), gilt `purchase` (alte Bedeutung).
 * Abnahme durch Steuerberater/Rudolf nötig (Preisbildung).
 */
export const purchasePriceBases = ['purchase', 'landed_cost'] as const;
export type PurchasePriceBasis = (typeof purchasePriceBases)[number];

export const purchasePriceBasisLabels: Record<PurchasePriceBasis, string> = { purchase: 'EK Hauptlieferant', landed_cost: 'Einstandspreis' };

/** Einstandspreis einer Zeile für „aus EK berechnen“ (geladen von `reporting`): Betrag in € (4 Stellen) oder Grund ohne Wert. */
export type LandedPriceBase = { kind: 'ok'; amount: string } | { kind: 'skip'; reason: string };

export const PURCHASE_PRICE_BASIS_TEXTS = {
  /** Präfix der Rechnung je Grundlage. */
  prefix: { purchase: 'EK', landed_cost: 'Einstandspreis' } as Record<PurchasePriceBasis, string>,
  skipped: (reason: string) => `übersprungen: ${reason}`,
  noLanded: 'übersprungen: kein Einstandspreis',
  noLandedReason: 'kein Einstandspreis',
} as const;

/** Deutsche Wörter der Aktionen (Feld-Leiste). */
export const listEditOperationLabels: Record<ListEditOperation, string> = {
  set: 'setzen auf',
  clear: 'leeren',
  add: 'hinzufügen',
  remove: 'entfernen',
  replace: 'ersetzen durch',
  increase_percent: '+ / − Prozent',
  increase_amount: '+ / − Betrag',
  round: 'runden',
  text_replace: 'ersetzen',
  prepend: 'voranstellen',
  append: 'anhängen',
  from_purchase_price: 'aus EK berechnen',
  swap: 'ersetzen A → B',
};

const operationsByType: Record<ListColumnType, readonly ListEditOperation[]> = {
  text: ['set', 'clear', 'text_replace', 'prepend', 'append'],
  // Story 5.84: `clear` nur für Zahlenfelder, die es ausdrücklich anbieten (Nebenkosten leeren = Variante erbt).
  number: ['set', 'increase_percent', 'increase_amount', 'round', 'clear'],
  // Story 5.30: `clear` nur für Geldfelder, die es ausdrücklich anbieten (Streich-/Referenzpreis entfernen).
  money: ['set', 'increase_percent', 'increase_amount', 'round', 'from_purchase_price', 'clear'],
  quantity: ['set', 'increase_percent', 'increase_amount', 'round'],
  percent: ['set', 'increase_percent', 'increase_amount', 'round'],
  date: ['set', 'clear'],
  datetime: ['set', 'clear'],
  boolean: ['set'],
  enum: ['set', 'clear'],
  status: ['set', 'clear'],
  // Story 5.89: setzen (genau diese Werte), leeren und ersetzen A → B zusätzlich – nur Felder, die es anbieten (Eigenschaften).
  multi_enum: ['set', 'clear', 'add', 'remove', 'replace', 'swap'],
  image: [],
  signals: [],
};

/** Operationen, die zu einem Spaltentyp passen (Register-Prüfung, Feld-Leiste). */
export function editOperationsFor(type: ListColumnType): readonly ListEditOperation[] {
  return operationsByType[type];
}

/**
 * Modi, die zu einer Spalte passen (Story 1.30): Formel nur für Zahlen, Beträge, Mengen, Prozent; Vorlage nur für Text;
 * „aus Spalte“ und „Werte einfügen“ für alles, was man setzen darf (außer Mehrfachauswahl und Bild); Regel-Tabelle für
 * jede Spalte mit Aktionen. Formel, Vorlage, „aus Spalte“ und Einfügen setzen einen Wert – sie brauchen `set`.
 */
export function editModesFor(column: Pick<ListColumn, 'type' | 'edit'>): ListEditMode[] {
  if (!column.edit) return [];
  const ops = column.edit.operations.filter((op) => operationsByType[column.type].includes(op));
  if (ops.length === 0) return [];
  const canSet = ops.includes('set');
  const settable = canSet && column.type !== 'multi_enum' && column.type !== 'image' && column.type !== 'signals';
  const out: ListEditMode[] = [];
  if (canSet && formulaFitsType(column.type)) out.push('formula');
  out.push('rule_table');
  if (canSet && column.type === 'text') out.push('template');
  if (settable) out.push('copy_from', 'paste');
  const allowed = column.edit.modes;
  return allowed ? out.filter((m) => allowed.includes(m)) : out;
}

/** Operationen, zu denen „Runden“ als Zusatz passt. */
const ROUNDABLE: readonly ListEditOperation[] = ['set', 'increase_percent', 'increase_amount'];

export const LIST_EDIT_TEXTS = {
  notEditable: (label: string) => `„${label}“ lässt sich in der Massenbearbeitung nicht ändern.`,
  columnUnknown: (column: string) => `Feld „${column}“ gibt es in dieser Liste nicht.`,
  operationInvalid: (label: string) => `Diese Aktion passt nicht zum Feld „${label}“. Bitte eine Aktion aus der Auswahl nehmen.`,
  operationDuplicate: (label: string) => `Das Feld „${label}“ steht schon in einer anderen Zeile der Feld-Leiste.`,
  valueMissing: (label: string) => `Bitte einen Wert für „${label}“ angeben.`,
  valueText: (label: string) => `Bitte einen Text für „${label}“ eingeben (höchstens 2.000 Zeichen).`,
  valueNumber: (label: string) => `Bitte eine Zahl für „${label}“ eingeben, z. B. 12,5 (höchstens 4 Nachkommastellen).`,
  valueMoney: (label: string) => `Bitte einen Betrag für „${label}“ eingeben, z. B. 99,90.`,
  valueMoney4: (label: string) => `Bitte einen Betrag für „${label}“ eingeben, z. B. 12,34.`,
  valueMoney4Positive: (label: string) => `Bitte einen Betrag größer als 0 für „${label}“ eingeben, z. B. 12,3456 – so lässt er sich nicht übernehmen.`,
  money4Rounded: (typed: string, rounded: string) => `${typed} auf 4 Nachkommastellen gerundet: ${rounded}`,
  money4RoundedGroup: 'Auf 4 Nachkommastellen gerundet',
  valuePercent: (label: string) => `Bitte eine Prozentzahl für „${label}“ eingeben, z. B. 5 oder -5.`,
  valueChoice: (label: string) => `Bitte einen Wert für „${label}“ aus der Auswahl nehmen.`,
  valueBoolean: (label: string) => `Bitte „ja“ oder „nein“ für „${label}“ wählen.`,
  valueDate: (label: string) => `Bitte ein Datum für „${label}“ wählen.`,
  findMissing: (label: string) => `Bitte angeben, welcher Text in „${label}“ ersetzt werden soll.`,
  multiSetEmpty: (label: string) => `Bitte mindestens einen Wert für „${label}“ wählen – zum Leeren „leeren“ nehmen.`,
  swapMissing: (label: string) => `Bitte für „${label}“ wählen, welcher Wert durch welchen ersetzt wird.`,
  roundInvalid: (label: string) => `Runden passt nicht zu dieser Aktion für „${label}“.`,
  currentNotNumber: (label: string) => `Der aktuelle Wert von „${label}“ ist keine Zahl; Rechnen ist hier nicht möglich.`,
  currentEmpty: (label: string) => `„${label}“ ist leer; zum Rechnen fehlt ein Ausgangswert.`,
  textTooLong: (label: string) => `Das Ergebnis für „${label}“ ist zu lang (höchstens 2.000 Zeichen).`,
  modeInvalid: (label: string) => `Dieser Modus passt nicht zum Feld „${label}“.`,
  factorInvalid: (label: string) =>
    `Bitte für „${label}“ einen Faktor größer als 0 eingeben, z. B. 1,8 (höchstens 4 Nachkommastellen).`,
  roundMissing: (label: string) => `Bitte für „${label}“ eine Preisendung wählen (,90 · ,99 · ganze).`,
  formulaMissing: (label: string) => `Bitte eine Formel für „${label}“ eingeben.`,
  templateMissing: (label: string) => `Bitte eine Vorlage für „${label}“ eingeben, z. B. {Hersteller} VHM-Fräser.`,
  templateColumn: (name: string) => `Unbekannte Spalte ‚${name}‘ in der Vorlage.`,
  templateBrace: 'In der Vorlage fehlt eine schließende „}“.',
  templateAmbiguous: (name: string) => `Die Spalte ‚${name}‘ gibt es mehrfach – bitte die Spaltenwahl („{“) nutzen.`,
  templateEmptyValue: (name: string) => `‚${name}‘ ist leer – die Vorlage braucht hier einen Wert.`,
  sourceMissing: (label: string) => `Bitte wählen, aus welcher Spalte „${label}“ den Wert bekommt.`,
  sourceUnknown: (column: string) => `Spalte „${column}“ gibt es in dieser Liste nicht.`,
  sourceSelf: 'Bitte eine andere Spalte als das Feld selbst wählen.',
  sourceType: (source: string, label: string) => `Werte aus „${source}“ passen nicht zu „${label}“.`,
  sourceCurrency: (source: string) => `Der Betrag in „${source}“ hat eine andere Währung.`,
  sourceValue: (source: string, label: string) => `Der Wert aus „${source}“ passt nicht zu „${label}“.`,
  rulesMissing: 'Bitte mindestens eine Regel anlegen (wenn … → Aktion).',
  ruleNested: 'In einer Regel geht nur eine einfache Aktion, Formel, Vorlage oder „aus Spalte“.',
  conditionMissing: 'Bitte für jede Regel eine Bedingung wählen.',
  pairsTooMany: (max: number) => `Höchstens ${max} Paare Suchen/Ersetzen auf einmal.`,
  noRule: 'keine Regel passt',
} as const;

/** Abwehr übergroßer Anfragen (keine fachliche Grenze). */
const MAX_PAIRS = 20;
const MAX_RULES = 20;

const MAX_TEXT = 2000;
/** Nachkommastellen für Zahl, Menge, Prozent. */
const NUMBER_SCALE = 4;
/** Kleinste Einheit der Beträge (EUR: Cent, AD-24). */
const MONEY_SCALE = 2;

// ---------------------------------------------------------------------------
// Eingabe

const textPairSchema = z.object({
  find: z.string().max(MAX_TEXT),
  value: z.string().max(MAX_TEXT),
});

/** Eine Aktion (ohne Spalte): auch der „dann“-Teil einer Regel. */
const actionFields = {
  op: z.enum(
    [
      'set',
      'clear',
      'add',
      'remove',
      'replace',
      'increase_percent',
      'increase_amount',
      'round',
      'text_replace',
      'prepend',
      'append',
      'from_purchase_price',
      'swap',
      'formula',
      'rule_table',
      'template',
      'copy_from',
      'paste',
    ],
    { error: 'Diese Aktion kennt das ERP nicht.' },
  ),
  value: z.union([z.string().max(MAX_TEXT), z.number(), z.boolean(), z.array(z.string().max(200)).max(200), z.null()]).optional(),
  find: z.string().max(MAX_TEXT).optional(),
  round: z.enum(listEditRounds).optional(),
  /** Suchen/Ersetzen: mehrere Paare nacheinander (Story 1.30); `find`/`value` bleibt als ein Paar gültig. */
  pairs: z.array(textPairSchema).max(MAX_PAIRS, LIST_EDIT_TEXTS.pairsTooMany(MAX_PAIRS)).optional(),
  ignoreCase: z.boolean().optional(),
  wholeWord: z.boolean().optional(),
  formula: z.string().max(MAX_TEXT).optional(),
  template: z.string().max(MAX_TEXT).optional(),
  /** „aus Spalte“: Schlüssel der Quellspalte. */
  source: z.string().max(120).optional(),
  /** „aus EK berechnen“: Faktor als Dezimaltext (z. B. „1,8“). */
  factor: z.string().max(30).optional(),
  /** „aus EK berechnen“: Grundlage (Story 5.85); fehlt sie, gilt `purchase`. */
  basis: z.enum(purchasePriceBases, { error: 'Bitte als Grundlage „EK Hauptlieferant“ oder „Einstandspreis“ wählen.' }).optional(),
};

export const listEditActionSchema = z.object(actionFields);
export type ListEditActionInput = z.input<typeof listEditActionSchema>;

/**
 * Eine Zeile der Feld-Leiste: Spalte, Aktion bzw. Modus und – je nachdem – Wert, Suchtext, Runden, Formel, Vorlage,
 * Quellspalte; optional „nur wenn …“ (`where`) und bei der Regel-Tabelle die Regeln mit „sonst“.
 */
export const listEditOperationSchema = z.object({
  column: z.string().min(1).max(120),
  ...actionFields,
  where: listFilterGroupSchema.optional(),
  rules: z
    .array(z.object({ when: listFilterGroupSchema, then: listEditActionSchema }))
    .max(MAX_RULES, `Höchstens ${MAX_RULES} Regeln.`)
    .optional(),
  otherwise: listEditActionSchema.nullable().optional(),
});
export type ListEditOperationInput = z.input<typeof listEditOperationSchema>;

export interface TextPair {
  find: string;
  value: string;
}

/** Teil einer Text-Vorlage: fester Text oder Spaltenbezug. */
export type TemplatePart = string | { key: string; label: string };

/** Geprüfte Aktion: Werte in der Form, mit der gerechnet wird. */
export type CheckedEditAction =
  | { column: string; op: 'set'; value: unknown; round?: ListEditRound }
  | { column: string; op: 'clear' }
  | { column: string; op: 'add' | 'remove' | 'replace'; values: string[] }
  /** Story 5.89: Mehrfachwert `find` durch `value` ersetzen (beides technische Werte aus `options`). */
  | { column: string; op: 'swap'; find: string; value: string }
  | { column: string; op: 'increase_percent'; percent: string; round?: ListEditRound }
  | { column: string; op: 'increase_amount'; amount: string; round?: ListEditRound }
  | { column: string; op: 'round'; round: ListEditRound }
  | { column: string; op: 'text_replace'; find: string; value: string; pairs?: TextPair[]; ignoreCase?: boolean; wholeWord?: boolean }
  | { column: string; op: 'prepend' | 'append'; value: string }
  /** Faktor als Dezimaltext mit Punkt (höchstens 4 Stellen); Endung Pflicht, gegen Settings prüft `reporting`. */
  | { column: string; op: 'from_purchase_price'; factor: string; round: ListEditRound; basis: PurchasePriceBasis }
  | { column: string; op: 'formula'; formula: ParsedFormula }
  | { column: string; op: 'template'; template: string; parts: TemplatePart[] }
  | { column: string; op: 'copy_from'; source: string; sourceLabel: string }
  | { column: string; op: 'paste' }
  | { column: string; op: 'rule_table'; rules: { when: ListFilterGroup; then: CheckedEditAction }[]; otherwise: CheckedEditAction | null };

/** Geprüfte Zeile der Feld-Leiste: Aktion und optional „nur wenn …“ (geprüft von `reporting`). */
export type CheckedEditOperation = CheckedEditAction & { where?: ListFilterGroup };

type EditColumn = Pick<ListColumn, 'key' | 'label' | 'type' | 'options' | 'edit'>;

/** Angabe der Nachkommastellen einer Geld-Spalte in der Massenbearbeitung (Story 5.60, `ListColumnEdit.scale`). */
export interface EditScaleHint {
  edit?: Pick<ListColumnEdit, 'scale'> | undefined;
}

/** Spalte der Liste, auf die sich Formel, Vorlage oder „aus Spalte“ beziehen dürfen (aus dem Register). */
export type EditRefColumn = Pick<ListColumn, 'key' | 'label' | 'type' | 'options'>;

/** Zusatz zur Prüfung (Story 1.30): alle Spalten der Liste für Bezüge. */
export interface EditCheckContext {
  columns?: readonly EditRefColumn[];
}

// ---------------------------------------------------------------------------
// Dezimalzahlen über BigInt

interface Dec {
  units: bigint;
  scale: number;
}

const DECIMAL = /^(-?)(\d{1,15})(?:[.,](\d{1,8}))?$/;

/** Dezimaltext (Punkt oder Komma, ohne Tausenderpunkte) bzw. ganze Zahl → Dec; sonst `null`. */
/** Betrag wie aus Excel kopiert: „12,40 €“, „€ 12,40“ (auch geschütztes Leerzeichen) – Euro-Zeichen und Leerzeichen fallen weg. */
function stripEuro(value: unknown): unknown {
  return typeof value === 'string' ? value.replace(/^[\s\u00a0\u202f]*€[\s\u00a0\u202f]*|[\s\u00a0\u202f]*€[\s\u00a0\u202f]*$/g, '') : value;
}

function parseDec(value: unknown): Dec | null {
  let text: string;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    // Nur exakt darstellbare Werte (ganze Zahlen); Bruchzahlen als Zahl nie (kein Gleitkomma).
    if (!Number.isSafeInteger(value)) return null;
    text = String(value);
  } else if (typeof value === 'string') {
    text = value.trim();
  } else if (typeof value === 'bigint') {
    return { units: value, scale: 0 };
  } else {
    return null;
  }
  const m = DECIMAL.exec(text);
  if (!m) return null;
  const frac = m[3] ?? '';
  const units = BigInt(`${m[2]}${frac}`);
  return { units: m[1] === '-' ? -units : units, scale: frac.length };
}

/** Auf `scale` bringen; mehr Nachkommastellen werden kaufmännisch gerundet (half-up, weg von null). */
function toScale(d: Dec, scale: number): bigint {
  if (d.scale <= scale) return d.units * 10n ** BigInt(scale - d.scale);
  return divHalfUp(d.units, 10n ** BigInt(d.scale - scale));
}

/** Ganze Zahl in Einheiten von 10^-scale → Text mit Punkt, ohne überflüssige Nullen. */
function formatUnits(units: bigint, scale: number): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(scale + 1, '0');
  const int = digits.slice(0, digits.length - scale);
  const frac = digits.slice(digits.length - scale).replace(/0+$/, '');
  return `${negative && (int !== '0' || frac !== '') ? '-' : ''}${int}${frac ? `.${frac}` : ''}`;
}

function scaleOf(type: ListColumnType): number {
  return type === 'money' ? 0 : NUMBER_SCALE;
}

/** Nachkommastellen einer Geld-Spalte mit `edit.scale: 4` (Story 5.60). */
const MONEY4_SCALE = 4;
/** Reine Ziffernfolge in einer scale-4-Spalte = Cent (Altbestand vor Story 5.60). */
const LEGACY_CENTS = /^-?\d{1,20}$/;
/** Eingabe einer scale-4-Spalte: höchstens 8 Nachkommastellen, kaufmännisch auf 4 gerundet (Story 5.60). */
const MONEY4_INPUT_SCALE_MAX = 8;

/** Geld-Spalte, die in der Massenbearbeitung mit 4 Nachkommastellen rechnet (Story 5.60)? */
function isMoney4(column: { type: ListColumnType } & EditScaleHint): boolean {
  return column.type === 'money' && column.edit?.scale === MONEY4_SCALE;
}

/** Wert einer scale-4-Spalte → Einheiten von 10⁻⁴ €; reine Ziffernfolge (auch ganze Zahl) = Cent (Altbestand). */
function money4Units(value: unknown): bigint | null {
  if (typeof value === 'bigint') return value * 100n;
  if (typeof value === 'number') return Number.isSafeInteger(value) ? BigInt(value) * 100n : null;
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (LEGACY_CENTS.test(text)) return BigInt(text) * 100n;
  const d = parseDec(text);
  if (!d || d.scale > MONEY4_SCALE) return null;
  return toScale(d, MONEY4_SCALE);
}

/** Einheiten von 10⁻⁴ € → kanonischer Text mit genau 4 Nachkommastellen („40.9000“); auch für `catalog` (Story 5.60). */
export function formatMoney4(units: bigint): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(MONEY4_SCALE + 1, '0');
  return `${negative ? '-' : ''}${digits.slice(0, -MONEY4_SCALE)}.${digits.slice(-MONEY4_SCALE)}`;
}

/**
 * Betrag einer Geld-Spalte der Massenbearbeitung als Einheiten von 10⁻⁴ € (Story 5.60): bei `edit.scale: 4` der
 * Euro-Dezimaltext (reine Ziffernfolge = Cent, Altbestand), sonst Cent × 100. Kein Betrag → `null`. Für Vergleiche in
 * `reporting` (Plausibilität), damit 4-Stellen-Werte und Cent exakt vergleichbar sind.
 */
export function editMoneyUnits4(column: { type: ListColumnType } & EditScaleHint, value: unknown): bigint | null {
  if (column.type !== 'money') return null;
  if (isMoney4(column)) return money4Units(value);
  const cents = currentUnits('money', value);
  return cents === null ? null : cents * 100n;
}

/** Aktueller Wert → Rechen-Einheiten der Spalte (scale 4: 10⁻⁴ €; sonst wie `currentUnits`). */
function unitsOf(column: { type: ListColumnType } & EditScaleHint, value: unknown): bigint | null {
  return isMoney4(column) ? money4Units(value) : currentUnits(column.type, value);
}

/** Rechen-Einheiten → Wert der Spalte (scale 4: genau 4 Nachkommastellen). */
function valueOf(column: { type: ListColumnType } & EditScaleHint, units: bigint): string {
  return isMoney4(column) ? formatMoney4(units) : unitsToValue(column.type, units);
}

/** Eingegebener Betrag/Zahl → Rechen-Einheiten der Spalte. */
function inputOf(column: { type: ListColumnType } & EditScaleHint, d: Dec): bigint {
  return isMoney4(column) ? toScale(d, MONEY4_SCALE) : inputUnits(column.type, d);
}

/** Höchstens erlaubte Nachkommastellen einer Eingabe (scale 4: bis 8, kaufmännisch auf 4 gerundet, Story 5.60). */
function inputScaleMax(column: { type: ListColumnType } & EditScaleHint): number {
  if (column.type !== 'money') return NUMBER_SCALE;
  return isMoney4(column) ? MONEY4_INPUT_SCALE_MAX : MONEY_SCALE;
}

/**
 * Hinweis für die Vorschau (Story 5.60, rein): Eingabe einer scale-4-Geldspalte mit mehr als 4 Nachkommastellen →
 * „12,34567 auf 4 Nachkommastellen gerundet: 12,3457 €“ (half-up); sonst `null` (keine Rundung, ungültig, andere Spalte).
 * Abnahme durch Steuerberater/Rudolf nötig (Preislogik).
 */
export function money4RoundingNote(column: { type: ListColumnType } & EditScaleHint, raw: unknown): string | null {
  if (!isMoney4(column) || typeof raw !== 'string') return null;
  const d = parseDec(stripEuro(raw));
  if (!d || d.scale <= MONEY4_SCALE || d.scale > MONEY4_INPUT_SCALE_MAX) return null;
  return LIST_EDIT_TEXTS.money4Rounded(raw.trim(), formatEditValue(column, formatMoney4(toScale(d, MONEY4_SCALE))));
}

/** Satz bei ungültigem Betrag/Zahl. */
function amountError(column: { label: string; type: ListColumnType } & EditScaleHint): string {
  if (column.type !== 'money') return LIST_EDIT_TEXTS.valueNumber(column.label);
  return isMoney4(column) ? LIST_EDIT_TEXTS.valueMoney4(column.label) : LIST_EDIT_TEXTS.valueMoney(column.label);
}

/** Nächstgelegener Wert mit der Endung ,90/,99/ganze (Gleichstand aufwärts); „1“ = ganzer Euro bzw. ganze Zahl. */
function roundTo(units: bigint, column: { type: ListColumnType } & EditScaleHint, round: ListEditRound): bigint {
  // money: Werte in Cent → ganzer Euro = 100; sonst (auch scale 4) Einheiten von 10^-4 → ganze Zahl = 10.000.
  const unit = column.type === 'money' && !isMoney4(column) ? 10n ** BigInt(MONEY_SCALE) : 10n ** BigInt(NUMBER_SCALE);
  return roundToEnding(units, unit, round);
}

/** Eingegebener Betrag/Zahl → Einheiten der Spalte (money: Euro → Cent). */
function inputUnits(type: ListColumnType, d: Dec): bigint {
  return type === 'money' ? toScale(d, MONEY_SCALE) : toScale(d, NUMBER_SCALE);
}

/** Aktueller Wert der Spalte → Einheiten (money: schon in Cent). */
function currentUnits(type: ListColumnType, value: unknown): bigint | null {
  const d = parseDec(value);
  if (!d) return null;
  return type === 'money' ? toScale(d, 0) : toScale(d, NUMBER_SCALE);
}

function unitsToValue(type: ListColumnType, units: bigint): string {
  return formatUnits(units, scaleOf(type));
}

// ---------------------------------------------------------------------------
// Prüfen

function choiceValues(column: EditColumn): Set<string> | null {
  return column.options ? new Set(column.options.map((o) => o.value)) : null;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Zeitpunkt nur als ISO 8601 mit Zeitzone (`2027-02-14T09:30:00Z` bzw. `+01:00`) und echtem Kalendertag. */
const ISO_INSTANT = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?(Z|[+-]\d{2}:\d{2})$/;

function isIsoInstant(text: string): boolean {
  const m = ISO_INSTANT.exec(text);
  return m !== null && isCalendarDate(m[1]!) && !Number.isNaN(Date.parse(text));
}

/** Kalenderdatum `JJJJ-MM-TT`, das es wirklich gibt (kein 31.02., kein Monat 13). */
function isCalendarDate(text: string): boolean {
  const m = ISO_DATE.exec(text);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d;
}

/** Prüft einen Wert für „setzen“ (auch einen von Hand überschriebenen Zellwert) und bringt ihn in Rechenform. */
export function parseEditValue(column: EditColumn, raw: unknown): { ok: true; value: unknown } | { ok: false; error: string } {
  const { label, type } = column;
  if (raw === undefined || raw === null || (typeof raw === 'string' && raw.trim() === '' && type !== 'text')) {
    return { ok: false, error: LIST_EDIT_TEXTS.valueMissing(label) };
  }
  switch (type) {
    case 'text': {
      if (typeof raw !== 'string' || raw.length > MAX_TEXT) return { ok: false, error: LIST_EDIT_TEXTS.valueText(label) };
      return { ok: true, value: raw };
    }
    case 'number':
    case 'quantity':
    case 'percent': {
      const d = parseDec(raw);
      if (!d || d.scale > NUMBER_SCALE) return { ok: false, error: LIST_EDIT_TEXTS.valueNumber(label) };
      return { ok: true, value: unitsToValue(type, inputUnits(type, d)) };
    }
    case 'money': {
      const d = parseDec(stripEuro(raw));
      if (!d || d.scale > inputScaleMax(column)) return { ok: false, error: amountError(column) };
      const units = inputOf(column, d);
      // Story 5.60: „setzen“/Hand-Wert des VK netto nur > 0 (auch nach dem Runden, z. B. 0,00004 → 0).
      if (isMoney4(column) && units <= 0n) return { ok: false, error: LIST_EDIT_TEXTS.valueMoney4Positive(label) };
      return { ok: true, value: valueOf(column, units) };
    }
    case 'enum':
    case 'status': {
      const allowed = choiceValues(column);
      if (typeof raw !== 'string' || (allowed && !allowed.has(raw))) return { ok: false, error: LIST_EDIT_TEXTS.valueChoice(label) };
      return { ok: true, value: raw };
    }
    case 'multi_enum': {
      const allowed = choiceValues(column);
      const list = Array.isArray(raw) ? raw : null;
      if (!list || list.some((v) => typeof v !== 'string' || (allowed && !allowed.has(v)))) {
        return { ok: false, error: LIST_EDIT_TEXTS.valueChoice(label) };
      }
      return { ok: true, value: unique(list as string[]) };
    }
    case 'boolean':
      if (typeof raw !== 'boolean') return { ok: false, error: LIST_EDIT_TEXTS.valueBoolean(label) };
      return { ok: true, value: raw };
    case 'date':
    case 'datetime':
      if (typeof raw !== 'string' || (type === 'date' ? !isCalendarDate(raw) : !isIsoInstant(raw))) {
        return { ok: false, error: LIST_EDIT_TEXTS.valueDate(label) };
      }
      return { ok: true, value: raw };
    default:
      return { ok: false, error: LIST_EDIT_TEXTS.notEditable(label) };
  }
}

function unique(values: readonly string[]): string[] {
  return [...new Set(values)];
}

/** Gleiche Mehrfachwerte unabhängig von der Reihenfolge? */
function sameMembers(before: unknown, values: unknown): boolean {
  if (!Array.isArray(before) || !Array.isArray(values)) return false;
  const a = new Set(before.map(String));
  const b = new Set(values.map(String));
  return a.size === b.size && [...a].every((v) => b.has(v));
}

/**
 * Prüft eine Zeile der Feld-Leiste gegen die Spalte: nur Spalten mit `edit`, nur deren Operationen bzw. die passenden
 * Modi (Story 1.30), Wert passend zum Typ. Formel, Vorlage und „aus Spalte“ prüfen ihre Bezüge gegen `ctx.columns`
 * (Register der Liste). Ergebnis: Operation in Rechenform oder ein Satz. `where` und die Bedingungen der Regel-Tabelle
 * gehen ungeprüft durch – sie prüft `reporting` gegen das Register (wie jeden Listen-Filter).
 */
export function checkEditOperation(
  column: EditColumn | undefined,
  input: z.output<typeof listEditOperationSchema>,
  ctx: EditCheckContext = {},
): { ok: true; operation: CheckedEditOperation } | { ok: false; error: string } {
  if (!column) return { ok: false, error: LIST_EDIT_TEXTS.columnUnknown(input.column) };
  if (!column.edit) return { ok: false, error: LIST_EDIT_TEXTS.notEditable(column.label) };
  const checked = checkAction(column, input, ctx, true);
  if (!checked.ok) return checked;
  if (input.op === 'rule_table') {
    const rules = input.rules ?? [];
    if (rules.length === 0) return { ok: false, error: LIST_EDIT_TEXTS.rulesMissing };
    const out: { when: ListFilterGroup; then: CheckedEditAction }[] = [];
    for (const rule of rules) {
      if (rule.when.items.length === 0) return { ok: false, error: LIST_EDIT_TEXTS.conditionMissing };
      const then = checkAction(column, { ...rule.then, column: input.column }, ctx, false);
      if (!then.ok) return then;
      out.push({ when: rule.when as ListFilterGroup, then: then.operation });
    }
    let otherwise: CheckedEditAction | null = null;
    if (input.otherwise) {
      const other = checkAction(column, { ...input.otherwise, column: input.column }, ctx, false);
      if (!other.ok) return other;
      otherwise = other.operation;
    }
    return { ok: true, operation: withWhere({ column: input.column, op: 'rule_table', rules: out, otherwise }, input.where) };
  }
  return { ok: true, operation: withWhere(checked.operation, input.where) };
}

function withWhere(operation: CheckedEditAction, where: z.output<typeof listFilterGroupSchema> | undefined): CheckedEditOperation {
  return where && where.items.length > 0 ? { ...operation, where: where as ListFilterGroup } : operation;
}

type ActionInput = z.output<typeof listEditActionSchema> & { column: string };

/** Eine Aktion prüfen (Feldzeile oder „dann“ einer Regel); `top`: Regel-Tabelle und Einfügen nur in der Feldzeile. */
function checkAction(
  column: EditColumn,
  input: ActionInput,
  ctx: EditCheckContext,
  top: boolean,
): { ok: true; operation: CheckedEditAction } | { ok: false; error: string } {
  const { label, type } = column;
  const key = input.column;
  if ((listEditModes as readonly string[]).includes(input.op)) {
    const mode = input.op as ListEditMode;
    if (!top && (mode === 'rule_table' || mode === 'paste')) return { ok: false, error: LIST_EDIT_TEXTS.ruleNested };
    if (!editModesFor(column).includes(mode)) return { ok: false, error: LIST_EDIT_TEXTS.modeInvalid(label) };
    if (input.round !== undefined) return { ok: false, error: LIST_EDIT_TEXTS.roundInvalid(label) };
    const columns = ctx.columns ?? [];
    switch (mode) {
      case 'formula': {
        if (input.formula === undefined || input.formula.trim() === '') return { ok: false, error: LIST_EDIT_TEXTS.formulaMissing(label) };
        const parsed = parseFormula(input.formula, columns);
        if (!parsed.ok) return parsed;
        return { ok: true, operation: { column: key, op: 'formula', formula: parsed.formula } };
      }
      case 'template': {
        if (input.template === undefined || input.template === '') return { ok: false, error: LIST_EDIT_TEXTS.templateMissing(label) };
        const parts = parseTemplate(input.template, columns);
        if (!parts.ok) return parts;
        return { ok: true, operation: { column: key, op: 'template', template: input.template, parts: parts.parts } };
      }
      case 'copy_from': {
        if (!input.source) return { ok: false, error: LIST_EDIT_TEXTS.sourceMissing(label) };
        const source = columns.find((c) => c.key === input.source);
        if (!source) return { ok: false, error: LIST_EDIT_TEXTS.sourceUnknown(input.source) };
        if (source.key === column.key) return { ok: false, error: LIST_EDIT_TEXTS.sourceSelf };
        if (!copyFits(source, column)) return { ok: false, error: LIST_EDIT_TEXTS.sourceType(source.label, label) };
        return { ok: true, operation: { column: key, op: 'copy_from', source: source.key, sourceLabel: source.label } };
      }
      case 'paste':
        return { ok: true, operation: { column: key, op: 'paste' } };
      case 'rule_table':
        // Regeln prüft `checkEditOperation` (braucht die Feldzeile).
        return { ok: true, operation: { column: key, op: 'rule_table', rules: [], otherwise: null } };
    }
  }
  const op = input.op as ListEditOperation;
  const allowed = column.edit!.operations.filter((o) => operationsByType[type].includes(o));
  if (!allowed.includes(op)) return { ok: false, error: LIST_EDIT_TEXTS.operationInvalid(label) };
  if (input.round !== undefined && op !== 'round' && op !== 'from_purchase_price' && !ROUNDABLE.includes(op)) {
    return { ok: false, error: LIST_EDIT_TEXTS.roundInvalid(label) };
  }
  const round = input.round !== undefined ? { round: input.round } : {};
  switch (op) {
    case 'set': {
      const parsed = parseEditValue(column, input.value);
      if (!parsed.ok) return parsed;
      // Story 5.89: „setzen auf“ ohne Werte würde still leeren – dafür gibt es „leeren“.
      if (type === 'multi_enum' && Array.isArray(parsed.value) && parsed.value.length === 0) return { ok: false, error: LIST_EDIT_TEXTS.multiSetEmpty(label) };
      return { ok: true, operation: { column: key, op: 'set', value: parsed.value, ...round } };
    }
    case 'clear':
      return { ok: true, operation: { column: key, op: 'clear' } };
    case 'add':
    case 'remove':
    case 'replace': {
      const parsed = parseEditValue(column, input.value);
      if (!parsed.ok) return parsed;
      return { ok: true, operation: { column: key, op, values: parsed.value as string[] } };
    }
    case 'swap': {
      // Story 5.89: genau ein Wert A (`find`) und ein Wert B (`value`), beide aus der Auswahl des Felds.
      if (typeof input.find !== 'string' || input.find === '' || typeof input.value !== 'string' || input.value === '') {
        return { ok: false, error: LIST_EDIT_TEXTS.swapMissing(label) };
      }
      const parsed = parseEditValue(column, [input.find, input.value]);
      if (!parsed.ok) return parsed;
      return { ok: true, operation: { column: key, op: 'swap', find: input.find, value: input.value } };
    }
    case 'increase_percent': {
      const d = parseDec(input.value);
      if (!d || d.scale > NUMBER_SCALE) return { ok: false, error: LIST_EDIT_TEXTS.valuePercent(label) };
      return { ok: true, operation: { column: key, op: 'increase_percent', percent: formatUnits(toScale(d, NUMBER_SCALE), NUMBER_SCALE), ...round } };
    }
    case 'increase_amount': {
      const d = parseDec(input.value);
      if (!d || d.scale > inputScaleMax(column)) return { ok: false, error: amountError(column) };
      return { ok: true, operation: { column: key, op: 'increase_amount', amount: valueOf(column, inputOf(column, d)), ...round } };
    }
    case 'round':
      if (input.round === undefined) return { ok: false, error: LIST_EDIT_TEXTS.roundInvalid(label) };
      return { ok: true, operation: { column: key, op: 'round', round: input.round } };
    case 'text_replace': {
      // Mehrere Paare (Story 1.30) oder ein Paar `find`/`value` (Story 1.21).
      const pairs = input.pairs ?? (input.find !== undefined || input.value !== undefined ? [{ find: input.find ?? '', value: input.value as string }] : []);
      if (pairs.length === 0 || pairs.some((p) => p.find === '')) return { ok: false, error: LIST_EDIT_TEXTS.findMissing(label) };
      if (pairs.some((p) => typeof p.value !== 'string')) return { ok: false, error: LIST_EDIT_TEXTS.valueText(label) };
      const first = pairs[0]!;
      return {
        ok: true,
        operation: {
          column: key,
          op: 'text_replace',
          find: first.find,
          value: first.value,
          ...(input.pairs ? { pairs: pairs.map((p) => ({ find: p.find, value: p.value })) } : {}),
          ...(input.ignoreCase ? { ignoreCase: true } : {}),
          ...(input.wholeWord ? { wholeWord: true } : {}),
        },
      };
    }
    case 'prepend':
    case 'append': {
      if (typeof input.value !== 'string' || input.value === '') return { ok: false, error: LIST_EDIT_TEXTS.valueText(label) };
      return { ok: true, operation: { column: key, op, value: input.value } };
    }
    case 'from_purchase_price': {
      const factor = parsePurchasePriceFactor(input.factor);
      if (factor === null) return { ok: false, error: LIST_EDIT_TEXTS.factorInvalid(label) };
      if (input.round === undefined) return { ok: false, error: LIST_EDIT_TEXTS.roundMissing(label) };
      return { ok: true, operation: { column: key, op: 'from_purchase_price', factor: formatFactor(factor), round: input.round, basis: input.basis ?? 'purchase' } };
    }
  }
}

const NUMERIC_TYPES: readonly ListColumnType[] = ['number', 'money', 'quantity', 'percent'];

/** Passt die Quellspalte zum Feld? Gleicher Typ; Zahlen untereinander (Betrag nur aus Betrag); Text aus allem außer Bild. */
function copyFits(source: EditRefColumn, target: EditColumn): boolean {
  if (source.type === 'image' || source.type === 'signals') return false;
  if (target.type === 'text') return true;
  if (source.type === 'multi_enum') return false;
  if (target.type === 'money' || source.type === 'money') return target.type === source.type;
  if (NUMERIC_TYPES.includes(target.type)) return NUMERIC_TYPES.includes(source.type);
  if (target.type === 'date') return source.type === 'date' || source.type === 'datetime';
  return source.type === target.type;
}

/** Text-Vorlage zerlegen: fester Text und `{Beschriftung}` (Spalten der Liste, Groß/Klein egal). */
export function parseTemplate(
  template: string,
  columns: readonly Pick<ListColumn, 'key' | 'label' | 'type'>[],
): { ok: true; parts: TemplatePart[] } | { ok: false; error: string } {
  const byLabel = new Map<string, Pick<ListColumn, 'key' | 'label' | 'type'>>();
  const norm = (t: string) => t.replace(/\s+/g, ' ').trim().toLocaleLowerCase('de-DE');
  const ambiguous = new Set<string>();
  for (const c of columns) {
    if (c.type === 'image' || c.type === 'signals') continue;
    const k = norm(c.label);
    if (byLabel.has(k) && byLabel.get(k)!.key !== c.key) ambiguous.add(k);
    else byLabel.set(k, c);
  }
  const parts: TemplatePart[] = [];
  let i = 0;
  while (i < template.length) {
    const open = template.indexOf('{', i);
    if (open < 0) {
      parts.push(template.slice(i));
      break;
    }
    if (open > i) parts.push(template.slice(i, open));
    const close = template.indexOf('}', open + 1);
    if (close < 0) return { ok: false, error: LIST_EDIT_TEXTS.templateBrace };
    const name = template.slice(open + 1, close);
    // Eindeutiger Bezug über den Spaltenschlüssel (`{modul.feld}`, fügt die Spaltenwahl bei gleicher Beschriftung ein).
    const byKey = columns.find((c) => c.key === name.trim() && c.type !== 'image' && c.type !== 'signals');
    if (!byKey && ambiguous.has(norm(name))) return { ok: false, error: LIST_EDIT_TEXTS.templateAmbiguous(name.trim()) };
    const column = byKey ?? byLabel.get(norm(name));
    if (!column) return { ok: false, error: LIST_EDIT_TEXTS.templateColumn(name.trim()) };
    parts.push({ key: column.key, label: column.label });
    i = close + 1;
  }
  return { ok: true, parts };
}

/** Spalten, deren Werte eine Operation braucht (Formel, Vorlage, „aus Spalte“, auch in Regeln). */
export function editOperationReferences(operation: CheckedEditAction): string[] {
  switch (operation.op) {
    case 'formula':
      return [...operation.formula.references];
    case 'template':
      return [...new Set(operation.parts.flatMap((p) => (typeof p === 'string' ? [] : [p.key])))];
    case 'copy_from':
      return [operation.source];
    case 'rule_table':
      return [...new Set([...operation.rules.map((r) => r.then), ...(operation.otherwise ? [operation.otherwise] : [])].flatMap(editOperationReferences))];
    default:
      return [];
  }
}

// ---------------------------------------------------------------------------
// Rechnen

/**
 * Neuer Wert einer Zelle nach der Operation (rein). Fehlt ein Ausgangswert zum Rechnen oder ist das Ergebnis
 * unzulässig, kommt ein Satz statt eines Werts.
 */
export function applyEditOperation(
  column: Pick<ListColumn, 'label' | 'type'> & Partial<Pick<ListColumn, 'options'>> & EditScaleHint,
  operation: CheckedEditAction,
  before: unknown,
  ctx: EditApplyContext = {},
): { ok: true; value: unknown; explain?: string; skip?: string } | { ok: false; error: string } {
  const { label, type } = column;
  switch (operation.op) {
    case 'formula': {
      const result = evaluateFormula(operation.formula, (ctx.values ?? {}) as Record<string, FormulaValue>, { type, currency: ctx.currency ?? null });
      if (!result.ok) return result;
      // Formel rechnet Beträge in Cent; scale-4-Spalte bekommt den Wert kanonisch mit 4 Stellen (Story 5.60).
      const units4 = isMoney4(column) && result.value !== null ? money4Units(result.value) : null;
      return { ok: true, value: units4 !== null ? formatMoney4(units4) : result.value, explain: result.explain };
    }
    case 'template':
      return applyTemplate(column, operation.parts, ctx);
    case 'copy_from':
      return copyValue(column, operation, ctx);
    case 'paste':
      // Eingefügte Werte kommen als Hand-Werte; ohne eingefügten Wert bleibt die Zeile, wie sie ist.
      return { ok: true, value: before };
    case 'rule_table': {
      const index = ctx.rule;
      const action = index === null || index === undefined ? null : index < operation.rules.length ? operation.rules[index]!.then : operation.otherwise;
      return action ? applyEditOperation(column, action, before, ctx) : { ok: true, value: before };
    }
    case 'set': {
      // Mehrfachwerte (Story 5.89): gleiche Werte in anderer Reihenfolge sind keine Änderung.
      if (type === 'multi_enum') return { ok: true, value: sameMembers(before, operation.value) ? before : operation.value };
      if (operation.round === undefined) return { ok: true, value: operation.value };
      const units = unitsOf(column, operation.value);
      if (units === null) return { ok: false, error: LIST_EDIT_TEXTS.valueNumber(label) };
      return { ok: true, value: valueOf(column, roundTo(units, column, operation.round)) };
    }
    case 'clear':
      return { ok: true, value: type === 'multi_enum' ? [] : null };
    case 'add': {
      const current = Array.isArray(before) ? before.map(String) : [];
      return { ok: true, value: unique([...current, ...operation.values]) };
    }
    case 'remove': {
      const current = Array.isArray(before) ? before.map(String) : [];
      const drop = new Set(operation.values);
      return { ok: true, value: current.filter((v) => !drop.has(v)) };
    }
    case 'replace':
      return { ok: true, value: unique(operation.values) };
    case 'swap': {
      // Fehlt A, bleibt die Liste, wie sie ist; sonst steht B an der Stelle von A (ohne Dublette).
      const current = Array.isArray(before) ? before.map(String) : [];
      if (!current.includes(operation.find)) return { ok: true, value: before ?? [] };
      return { ok: true, value: unique(current.map((v) => (v === operation.find ? operation.value : v))) };
    }
    case 'increase_percent':
    case 'increase_amount':
    case 'round': {
      if (before === null || before === undefined || before === '') return { ok: false, error: LIST_EDIT_TEXTS.currentEmpty(label) };
      const units = unitsOf(column, before);
      if (units === null) return { ok: false, error: LIST_EDIT_TEXTS.currentNotNumber(label) };
      let next = units;
      if (operation.op === 'increase_percent') {
        // x · (100 + p) / 100, p mit 4 Nachkommastellen: x · (100·10^4 + p·10^4) / (100·10^4), half-up.
        const p = parseDec(operation.percent)!;
        const factor = 100n * 10n ** BigInt(NUMBER_SCALE) + toScale(p, NUMBER_SCALE);
        next = divHalfUp(units * factor, 100n * 10n ** BigInt(NUMBER_SCALE));
      } else if (operation.op === 'increase_amount') {
        next = units + unitsOf(column, operation.amount)!;
      }
      if (operation.round !== undefined) next = roundTo(next, column, operation.round);
      return { ok: true, value: valueOf(column, next) };
    }
    case 'text_replace': {
      const text = typeof before === 'string' ? before : '';
      const out = replaceText(text, operation);
      if (out.length > MAX_TEXT) return { ok: false, error: LIST_EDIT_TEXTS.textTooLong(label) };
      return { ok: true, value: before === null || before === undefined ? null : out };
    }
    case 'prepend':
    case 'append': {
      const text = typeof before === 'string' ? before : '';
      const out = operation.op === 'prepend' ? `${operation.value}${text}` : `${text}${operation.value}`;
      if (out.length > MAX_TEXT) return { ok: false, error: LIST_EDIT_TEXTS.textTooLong(label) };
      return { ok: true, value: out };
    }
    case 'from_purchase_price': {
      // Übersprungen (kein EK, kein Kurs, kein Einstandspreis, Ergebnis ≤ 0): Wert bleibt, Grund als `skip` – kein Fehler.
      // Story 5.85: Grundlage EK oder Einstandspreis; derselbe Kern rechnet beide (Einstandspreis immer in EUR).
      const basis = operation.basis ?? 'purchase';
      let base: PurchasePriceBase | null;
      if (basis === 'landed_cost') {
        const landed = ctx.landed;
        if (!landed) return { ok: true, value: before, skip: PURCHASE_PRICE_BASIS_TEXTS.noLanded };
        if (landed.kind === 'skip') return { ok: true, value: before, skip: PURCHASE_PRICE_BASIS_TEXTS.skipped(landed.reason) };
        base = { amount: landed.amount, currency: 'EUR' };
      } else {
        base = ctx.base ?? null;
      }
      const result = computePriceFromPurchase({
        base,
        priceCurrency: ctx.currency ?? 'EUR',
        rates: ctx.rates ?? {},
        factor: operation.factor,
        round: operation.round,
      });
      if (result.kind === 'skip') return { ok: true, value: before, skip: result.reason };
      return { ok: true, value: isMoney4(column) ? formatMoney4(result.units4) : result.cents.toString(), explain: purchaseExplain(basis, result.explain, base) };
    }
  }
}

/** Zusatz zum Rechnen (Story 1.30): Werte der Bezugsspalten, Währung der Zeile, welche Regel gegriffen hat. */
export interface EditApplyContext {
  /** Werte der Bezugsspalten der Zeile (Spaltenschlüssel → Wert wie in der Liste; Betrag `{ amountMinor, currency }`). */
  values?: Readonly<Record<string, unknown>>;
  /** Spalten der Liste (Vorlage: Anzeige der Bezugswerte). */
  columns?: ReadonlyMap<string, EditRefColumn>;
  /** Währung der Zeile bei Beträgen (Formel, „aus Spalte“). */
  currency?: string | null;
  /** Regel-Tabelle: Stelle der ersten passenden Regel, `rules.length` = „sonst“, `null` = keine passt. */
  rule?: number | null;
  /** „aus EK berechnen“: Basisbetrag der Zeile (heute EK des Hauptlieferanten, geladen von `reporting`); `null` = keiner. */
  base?: PurchasePriceBase | null;
  /** „aus EK berechnen“: EZB-Kurse je Fremdwährung (Fremdwährung je 1 EUR, mit Kursdatum). */
  rates?: Readonly<Record<string, PurchasePriceRate>>;
  /** „aus EK berechnen“ mit Grundlage Einstandspreis (Story 5.85): Einstandspreis der Zeile (geladen von `reporting`). */
  landed?: LandedPriceBase | null;
}

/**
 * Rechnung mit Präfix der Grundlage („EK 21,40 € × 1,8 = …“). Beim Einstandspreis (immer EUR, kein Kursschritt) steht
 * vor dem ersten „ × “ der auf Cent gerundete Betrag („Einstandspreis 22,84 € × 1,8 = 41,12 → 40,90 €“); gerechnet hat
 * der Kern mit allen 4 Stellen.
 */
function purchaseExplain(basis: PurchasePriceBasis, explain: string, base: PurchasePriceBase | null): string {
  const prefix = PURCHASE_PRICE_BASIS_TEXTS.prefix[basis];
  if (basis !== 'landed_cost' || !base?.amount) return `${prefix} ${explain}`;
  const at = explain.indexOf(' × ');
  const units4 = decimalToUnits4(base.amount);
  if (at < 0 || units4 === null) return `${prefix} ${explain}`;
  const cents = divHalfUp(units4, 100n);
  const shown = germanDecimal(`${cents / 100n}.${(cents % 100n).toString().padStart(2, '0')}`);
  return `${prefix} ${shown} €${explain.slice(at)}`;
}

/** Grundlagen, die eine Operation „aus EK berechnen“ nutzt (auch in Regeln): `reporting` lädt nur, was gebraucht wird. */
export function purchasePriceBasesUsed(operation: CheckedEditAction): Set<PurchasePriceBasis> {
  if (operation.op === 'from_purchase_price') return new Set([operation.basis ?? 'purchase']);
  if (operation.op !== 'rule_table') return new Set();
  const out = new Set<PurchasePriceBasis>();
  for (const a of [...operation.rules.map((r) => r.then), ...(operation.otherwise ? [operation.otherwise] : [])]) for (const b of purchasePriceBasesUsed(a)) out.add(b);
  return out;
}

/** Nutzt die Operation „aus EK berechnen“ (auch als „dann“ einer Regel)? Dann braucht `reporting` den EK je Zeile. */
export function usesPurchasePrice(operation: CheckedEditAction): boolean {
  if (operation.op === 'from_purchase_price') return true;
  if (operation.op !== 'rule_table') return false;
  return [...operation.rules.map((r) => r.then), ...(operation.otherwise ? [operation.otherwise] : [])].some(usesPurchasePrice);
}

/** Endungen, die eine Operation „aus EK berechnen“ nutzt (auch in Regeln), zur Prüfung gegen die Settings. */
export function purchasePriceRounds(operation: CheckedEditAction): ListEditRound[] {
  if (operation.op === 'from_purchase_price') return [operation.round];
  if (operation.op !== 'rule_table') return [];
  return [...operation.rules.map((r) => r.then), ...(operation.otherwise ? [operation.otherwise] : [])].flatMap(purchasePriceRounds);
}

/** Faktoren, die eine Operation „aus EK berechnen“ nutzt (auch in Regeln), zur Prüfung gegen die Obergrenze aus den Settings. */
export function purchasePriceFactors(operation: CheckedEditAction): string[] {
  if (operation.op === 'from_purchase_price') return [operation.factor];
  if (operation.op !== 'rule_table') return [];
  return [...operation.rules.map((r) => r.then), ...(operation.otherwise ? [operation.otherwise] : [])].flatMap(purchasePriceFactors);
}

const WORD_CHAR = /[\p{L}\p{N}]/u;

/** Suchen/Ersetzen: Paare nacheinander; optional Groß/Klein egal und nur ganze Wörter (Wortzeichen: Buchstaben, Ziffern). */
function replaceText(text: string, operation: Extract<CheckedEditAction, { op: 'text_replace' }>): string {
  const pairs = operation.pairs ?? [{ find: operation.find, value: operation.value }];
  let out = text;
  for (const pair of pairs) {
    if (!operation.ignoreCase && !operation.wholeWord) {
      out = out.split(pair.find).join(pair.value);
      continue;
    }
    const haystack = operation.ignoreCase ? out.toLocaleLowerCase('de-DE') : out;
    const needle = operation.ignoreCase ? pair.find.toLocaleLowerCase('de-DE') : pair.find;
    // Kleinschreibung ändert die Länge nur in seltenen Fällen (z. B. „İ“); dann ohne Groß/Klein-Vergleich weiter.
    const comparable = haystack.length === out.length && needle.length === pair.find.length;
    const h = comparable ? haystack : out;
    const n = comparable ? needle : pair.find;
    let result = '';
    let i = 0;
    for (let at = h.indexOf(n, i); at >= 0; at = h.indexOf(n, i)) {
      const end = at + n.length;
      const whole = !operation.wholeWord || (!WORD_CHAR.test(out[at - 1] ?? '') && !WORD_CHAR.test(out[end] ?? ''));
      if (whole) {
        result += out.slice(i, at) + pair.value;
        i = end;
      } else {
        result += out.slice(i, at + 1);
        i = at + 1;
      }
    }
    out = result + out.slice(i);
  }
  return out;
}

/** Wert einer Spalte als Text in einer Vorlage: Zahlen deutsch ohne Einheit, Beträge mit Währung, Auswahl als Text. */
function templateText(column: EditRefColumn | undefined, value: unknown): string {
  if (!column) return String(value);
  switch (column.type) {
    case 'number':
    case 'quantity':
    case 'percent': {
      const d = parseDec(value);
      return d ? germanDecimal(formatUnits(d.units, d.scale)) : String(value);
    }
    case 'date':
      return formatListDate(String(value));
    case 'text':
      return String(value);
    default:
      return formatListValue(column, value);
  }
}

function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0);
}

function applyTemplate(
  column: Pick<ListColumn, 'label'>,
  parts: readonly TemplatePart[],
  ctx: EditApplyContext,
): { ok: true; value: unknown } | { ok: false; error: string } {
  let out = '';
  for (const part of parts) {
    if (typeof part === 'string') {
      out += part;
      continue;
    }
    const value = ctx.values && Object.hasOwn(ctx.values, part.key) ? ctx.values[part.key] : undefined;
    // Nie halb leere Texte: Fehlt ein Bezugswert, wird die Zeile nicht geändert (Satz statt Wert).
    if (isEmptyValue(value)) return { ok: false, error: LIST_EDIT_TEXTS.templateEmptyValue(part.label) };
    out += templateText(ctx.columns?.get(part.key), value);
  }
  if (out.length > MAX_TEXT) return { ok: false, error: LIST_EDIT_TEXTS.textTooLong(column.label) };
  return { ok: true, value: out };
}

/** „aus Spalte“: Wert der Quellspalte in die Form des Felds bringen (leer bleibt leer). */
function copyValue(
  column: Pick<ListColumn, 'label' | 'type'> & Partial<Pick<ListColumn, 'options'>> & EditScaleHint,
  operation: Extract<CheckedEditAction, { op: 'copy_from' }>,
  ctx: EditApplyContext,
): { ok: true; value: unknown } | { ok: false; error: string } {
  const raw = ctx.values && Object.hasOwn(ctx.values, operation.source) ? ctx.values[operation.source] : undefined;
  if (isEmptyValue(raw)) return { ok: true, value: column.type === 'multi_enum' ? [] : null };
  const source = ctx.columns?.get(operation.source);
  const invalid = { ok: false as const, error: LIST_EDIT_TEXTS.sourceValue(operation.sourceLabel, column.label) };
  if (column.type === 'text') {
    const text = templateText(source, raw);
    return text.length > MAX_TEXT ? { ok: false, error: LIST_EDIT_TEXTS.textTooLong(column.label) } : { ok: true, value: text };
  }
  if (column.type === 'money') {
    if (!isListMoneyValue(raw) || !/^-?\d{1,20}$/.test(raw.amountMinor)) return invalid;
    // Währung der Zeile unbekannt oder anders: kein stilles Übernehmen der kleinsten Einheit einer anderen Währung.
    if (ctx.currency !== undefined && raw.currency !== ctx.currency) return { ok: false, error: LIST_EDIT_TEXTS.sourceCurrency(operation.sourceLabel) };
    const minor = findCurrencyMinorUnits(raw.currency);
    if (minor === null) return invalid;
    // scale-4-Spalte (Story 5.60): kleinste Einheit → 4 Nachkommastellen.
    if (isMoney4(column)) return minor <= MONEY4_SCALE ? { ok: true, value: formatMoney4(BigInt(raw.amountMinor) * 10n ** BigInt(MONEY4_SCALE - minor)) } : invalid;
    return { ok: true, value: raw.amountMinor };
  }
  if (column.type === 'enum' || column.type === 'status' || column.type === 'boolean') {
    // Nur Werte, die das Feld kennt (andere Auswahlliste der Quelle → Satz statt Fehler beim Ausführen).
    const parsed = parseEditValue({ key: '', label: column.label, type: column.type, options: column.options }, raw);
    return parsed.ok ? parsed : invalid;
  }
  if (NUMERIC_TYPES.includes(column.type)) {
    const d = parseDec(raw);
    if (!d) return invalid;
    return { ok: true, value: unitsToValue(column.type, toScale(d, NUMBER_SCALE)) };
  }
  if (column.type === 'date') {
    const text = String(raw);
    // Zeitpunkt → fachlicher Tag in Berlin wie in der Liste angezeigt.
    if (isCalendarDate(text)) return { ok: true, value: text };
    if (!isIsoInstant(text)) return invalid;
    const day = berlinDay(text);
    return day ? { ok: true, value: day } : invalid;
  }
  return { ok: true, value: raw };
}

/** Kalendertag eines Zeitpunkts in Europe/Berlin (`JJJJ-MM-TT`). */
function berlinDay(instant: string): string | null {
  const d = new Date(instant);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  return /^\d{4}-\d{2}-\d{2}$/.test(parts) ? parts : null;
}

/**
 * Gleicher Wert? Zahlen nach Zahlenwert („12.50“ = „12.5“ = 12.5 als ganze Zahl), Listen nach Inhalt in Reihenfolge,
 * sonst streng gleich; `null` und `undefined` gelten gleich. `scale` = `edit.scale` der Spalte (Story 5.60: bei 4
 * „40.9000“ = „40.90“ = Altbestand „4090“ Cent).
 */
export function sameEditValue(type: ListColumnType, a: unknown, b: unknown, scale?: number): boolean {
  const na = a === undefined ? null : a;
  const nb = b === undefined ? null : b;
  if (na === null || nb === null) return na === nb;
  if (type === 'number' || type === 'money' || type === 'quantity' || type === 'percent') {
    const column = { type, edit: scale === MONEY4_SCALE ? { scale: MONEY4_SCALE } as const : undefined };
    const x = unitsOf(column, na);
    const y = unitsOf(column, nb);
    if (x !== null && y !== null) return x === y;
  }
  if (Array.isArray(na) && Array.isArray(nb)) return na.length === nb.length && na.every((v, i) => v === nb[i]);
  return na === nb;
}

// ---------------------------------------------------------------------------
// Beschreiben

function signed(text: string): { sign: string; abs: string } {
  return text.startsWith('-') ? { sign: '−', abs: text.slice(1) } : { sign: '+', abs: text };
}

const numberFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 4 });

function germanDecimal(text: string): string {
  // Ohne Gleitkomma: Ganzzahlteil mit Tausenderpunkten, Nachkommastellen unverändert.
  const negative = text.startsWith('-');
  const [int, frac] = (negative ? text.slice(1) : text).split('.');
  const grouped = numberFormat.format(BigInt(int!));
  return `${negative ? '−' : ''}${grouped}${frac ? `,${frac}` : ''}`;
}

/**
 * Wert einer Zelle als Text (Grid, Beschreibung); Beträge in Cent, bei `edit.scale: 4` (Story 5.60) Euro mit
 * mindestens 2, höchstens 4 Nachkommastellen („38,90 €“, „41,9328 €“).
 */
export function formatEditValue(column: Pick<ListColumn, 'type' | 'options'> & EditScaleHint, value: unknown): string {
  if (column.type === 'multi_enum' && Array.isArray(value) && value.length === 0) return '–';
  if ((column.type === 'number' || column.type === 'quantity' || column.type === 'percent') && typeof value === 'string' && parseDec(value)) {
    const text = germanDecimal(value.replace(',', '.'));
    return column.type === 'quantity' ? `${text} ${PIECE_UNIT}` : column.type === 'percent' ? `${text} %` : text;
  }
  if (isMoney4(column) && (typeof value === 'string' || typeof value === 'number')) {
    const units = money4Units(value);
    if (units !== null) {
      const negative = units < 0n;
      const abs = negative ? -units : units;
      const scale = 10n ** BigInt(MONEY4_SCALE);
      let frac = (abs % scale).toString().padStart(MONEY4_SCALE, '0');
      while (frac.length > MONEY_SCALE && frac.endsWith('0')) frac = frac.slice(0, -1);
      return `${negative ? '−' : ''}${germanDecimal((abs / scale).toString())},${frac} €`;
    }
  }
  if (column.type === 'money' && typeof value === 'string' && parseDec(value)) {
    const units = currentUnits('money', value)!;
    const negative = units < 0n;
    const abs = negative ? -units : units;
    const euros = germanDecimal((abs / 100n).toString());
    return `${negative ? '−' : ''}${euros},${(abs % 100n).toString().padStart(2, '0')} €`;
  }
  return formatListValue(column, value);
}

/** Satz einer Zeile der Feld-Leiste, z. B. „Belegsprache · setzen auf · Englisch“ oder „VK netto · + 5 % · auf ,90“. */
export function describeEditOperation(
  column: Pick<ListColumn, 'label' | 'type' | 'options'> & EditScaleHint,
  operation: CheckedEditAction,
  /** `true`: frei eingegebenen Text nicht nennen (gespeicherte Kurztexte ohne Personendaten, AD-11). */
  hideFreeText = false,
): string {
  return [column.label, describeEditAction(column, operation, hideFreeText)].join(' · ');
}

/** Satz einer Aktion ohne Feldnamen, z. B. „+ 5 % · runden auf ,90“ (auch „dann“ einer Regel). */
export function describeEditAction(column: Pick<ListColumn, 'label' | 'type' | 'options'> & EditScaleHint, operation: CheckedEditAction, hideFreeText = false): string {
  const parts: string[] = [];
  const free = (text: string) => (hideFreeText ? '„…“' : `„${text}“`);
  const roundPart = (r: ListEditRound | undefined) => (r ? [`runden ${listEditRoundLabels[r]}`] : []);
  switch (operation.op) {
    case 'set':
      parts.push(
        listEditOperationLabels.set,
        hideFreeText && column.type === 'text' ? free('') : formatEditValue(column, operation.value),
        ...roundPart(operation.round),
      );
      break;
    case 'clear':
      parts.push(listEditOperationLabels.clear);
      break;
    case 'add':
    case 'remove':
    case 'replace':
      parts.push(listEditOperationLabels[operation.op], operation.values.map((v) => optionLabel(column, v)).join(', ') || '–');
      break;
    case 'swap':
      parts.push(`ersetzen ${optionLabel(column, operation.find)} → ${optionLabel(column, operation.value)}`);
      break;
    case 'increase_percent': {
      const { sign, abs } = signed(operation.percent);
      parts.push(`${sign} ${germanDecimal(abs)} %`, ...roundPart(operation.round));
      break;
    }
    case 'increase_amount': {
      const { sign, abs } = signed(operation.amount);
      parts.push(`${sign} ${formatEditValue(column, abs)}`, ...roundPart(operation.round));
      break;
    }
    case 'round':
      parts.push(`runden ${listEditRoundLabels[operation.round]}`);
      break;
    case 'from_purchase_price':
      parts.push(
        listEditOperationLabels.from_purchase_price,
        `${purchasePriceBasisLabels[operation.basis ?? 'purchase']} × ${germanDecimal(operation.factor)}`,
        listEditRoundLabels[operation.round],
      );
      break;
    case 'text_replace': {
      const pairs = operation.pairs ?? [{ find: operation.find, value: operation.value }];
      parts.push(pairs.map((p) => `${free(p.find)} ersetzen durch ${free(p.value)}`).join(', '));
      if (operation.wholeWord) parts.push('nur ganzes Wort');
      if (operation.ignoreCase) parts.push('Groß/Klein egal');
      break;
    }
    case 'prepend':
    case 'append':
      parts.push(listEditOperationLabels[operation.op], free(operation.value));
      break;
    case 'formula':
      parts.push(`Formel ${operation.formula.text.trim()}`);
      break;
    case 'template':
      parts.push(`Vorlage ${free(operation.template)}`);
      break;
    case 'copy_from':
      parts.push(`Wert aus „${operation.sourceLabel}“`);
      break;
    case 'paste':
      parts.push('eingefügte Werte');
      break;
    case 'rule_table': {
      const n = operation.rules.length;
      parts.push(`Regel-Tabelle (${n === 1 ? '1 Regel' : `${n} Regeln`}${operation.otherwise ? ', sonst …' : ''})`);
      break;
    }
  }
  return parts.join(' · ');
}
